// src/pages/dealer/DealerDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDealerAuth } from "../../context/DealerAuthContext";
import { dealerAPI } from "../../services/dealerAPI";
import "./DealerDashboard.css";
 
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
 
export default function DealerDashboard() {
  const { dealer } = useDealerAuth();
  const navigate   = useNavigate();
 
  const [listings,       setListings]       = useState([]);
  const [offers,         setOffers]         = useState([]);
  const [passes,         setPasses]         = useState([]);
  const [credits,        setCredits]        = useState([]);
  const [myCarListings,  setMyCarListings]  = useState([]);
  const [biddingPass,    setBiddingPass]    = useState(null);
  const [listingCredits, setListingCredits] = useState(0);
  const [loading,        setLoading]        = useState(true);
 
  useEffect(() => {
    Promise.all([
      dealerAPI.getListings(),
      dealerAPI.getMyOffers(),
      dealerAPI.getPassPurchaseHistory(),
      dealerAPI.getCreditPurchaseHistory(),
      dealerAPI.getMyCarListings(),
      dealerAPI.getBiddingPassPlans(),
      dealerAPI.getListingCreditPlans(),
    ])
      .then(([lr, or, pr, cr, clr, bpr, lcr]) => {
        setListings(lr.data.data     || []);
        setOffers(or.data.data       || []);
        setPasses(pr.data?.data      || []);
        setCredits(cr.data?.data     || []);
        setMyCarListings(clr.data?.data || []);
        setBiddingPass({
          has_access: bpr.data?.has_access,
          expires_at: bpr.data?.expires_at,
        });
        setListingCredits(lcr.data?.available_credits ?? 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);
 
  // ── Payment stats ────────────────────────────────────────────────────────
  const passSpend   = passes.filter(p => p.status === 'success').reduce((s, p) => s + Number(p.amount || 0), 0);
  const creditSpend = credits.filter(c => c.status === 'success').reduce((s, c) => s + Number(c.amount || 0), 0);
  const bidPaid     = offers
    .filter(o => o.payment_status === 'success' || o.payment_status === 'paid')
    .reduce((s, o) => s + Number(o.offer_price || 0), 0);
  const totalSpend  = passSpend + creditSpend + bidPaid;
 
  // ── Stat cards ───────────────────────────────────────────────────────────
  const stats = [
    { label: "Active Listings",   value: listings.length,                               icon: "🚗", color: "#003566" },
    { label: "My Offers Placed",  value: offers.length,                                 icon: "🏷️", color: "#0057b7" },
    { label: "Winning Bids",      value: offers.filter(o => o.is_highest).length,       icon: "🏆", color: "#059669" },
    { label: "Accepted Deals",    value: offers.filter(o => o.status === "accepted").length, icon: "✅", color: "#7c3aed" },
  ];
 
  const recentListings = listings.slice(0, 4);
 
  if (loading) return <div className="dd-loading">Loading dashboard…</div>;
 
  return (
    <div className="dd-root">
 
      {/* ── Welcome ── */}
      <div className="dd-welcome">
        <div>
          <h1 className="dd-welcome-title">Welcome back, {dealer?.name || "Dealer"} 👋</h1>
          <p className="dd-welcome-sub">Here's what's happening in the auction today.</p>
        </div>
        <button className="dd-cta" onClick={() => navigate("/dealer/listings")}>Browse Listings →</button>
      </div>
 
      {/* ── Bidding Pass + Listing Credits Banner ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
 
        {/* Bidding Pass */}
        <div style={{
          background: biddingPass?.has_access ? 'linear-gradient(135deg,#003566,#001d3d)' : '#fff1f1',
          border: biddingPass?.has_access ? '2px solid #FFD60A' : '1px solid #fca5a5',
          borderRadius: 14, padding: '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <p style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 15, color: biddingPass?.has_access ? '#FFD60A' : '#dc2626' }}>
              🔨 Bidding Pass
            </p>
            {biddingPass?.has_access ? (
              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                Active · Expires {new Date(biddingPass.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>No active pass — buy one to bid</p>
            )}
          </div>
          <button
            onClick={() => navigate('/dealer/listings')}
            style={{
              padding: '7px 14px',
              background: biddingPass?.has_access ? '#FFD60A' : '#dc2626',
              color: biddingPass?.has_access ? '#003566' : '#fff',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {biddingPass?.has_access ? 'Bid Now →' : 'Buy Pass'}
          </button>
        </div>
 
        {/* Listing Credits */}
        <div style={{
          background: listingCredits > 0 ? '#f0fdf4' : '#fff1f1',
          border: `1px solid ${listingCredits > 0 ? '#86efac' : '#fca5a5'}`,
          borderRadius: 14, padding: '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <p style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 15, color: listingCredits > 0 ? '#166534' : '#dc2626' }}>
              🏷️ Listing Credits
            </p>
            <p style={{ margin: 0, fontSize: 12, color: listingCredits > 0 ? '#166534' : '#dc2626' }}>
              {listingCredits > 0
                ? `${listingCredits} credit${listingCredits !== 1 ? 's' : ''} available`
                : 'No credits — buy to list your car'}
            </p>
          </div>
          <button
            onClick={() => navigate('/dealer/sell-car')}
            style={{
              padding: '7px 14px',
              background: listingCredits > 0 ? '#003566' : '#dc2626',
              color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {listingCredits > 0 ? 'List Car →' : 'Buy Credits'}
          </button>
        </div>
      </div>
 
      {/* ── Stats ── */}
      <div className="dd-stats">
        {stats.map((s) => (
          <div className="dd-stat" key={s.label}>
            <div className="dd-stat-icon" style={{ background: s.color + "18" }}>
              <span>{s.icon}</span>
            </div>
            <div>
              <div className="dd-stat-val" style={{ color: s.color }}>{s.value}</div>
              <div className="dd-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>
 
      {/* ── Payment Summary ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: '#003566', margin: 0 }}>💰 Payment Summary</h2>
          <button
            onClick={() => navigate('/dealer/payments')}
            style={{ background: 'none', border: 'none', color: '#0057b7', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            View All Payments →
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          {[
            {
              icon: '🔨', label: 'Bidding Passes',
              value: fmt(passSpend),
              sub: `${passes.filter(p => p.status === 'success').length} purchases`,
              bg: '#ede9fe', color: '#7c3aed',
            },
            {
              icon: '🏷️', label: 'Listing Credits',
              value: fmt(creditSpend),
              sub: `${credits.filter(c => c.status === 'success').length} purchases`,
              bg: '#dbeafe', color: '#0057b7',
            },
            {
              icon: '🚗', label: 'Car Bid Payments',
              value: fmt(bidPaid),
              sub: `${offers.filter(o => o.payment_status === 'success' || o.payment_status === 'paid').length} paid`,
              bg: '#dcfce7', color: '#059669',
            },
            {
              icon: '💰', label: 'Total Spent',
              value: fmt(totalSpend),
              sub: 'all time',
              highlight: true,
            },
          ].map(card => (
            <div
              key={card.label}
              style={{
                background: card.highlight ? 'linear-gradient(135deg,#003566,#001d3d)' : '#fff',
                border: card.highlight ? '2px solid #FFD60A' : '1px solid #e5e7eb',
                borderRadius: 14, padding: '18px',
                boxShadow: '0 2px 10px rgba(0,53,102,0.07)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: card.highlight ? 'rgba(255,214,10,0.15)' : card.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>
                  {card.icon}
                </div>
                <span style={{ fontSize: 12, color: card.highlight ? 'rgba(255,255,255,0.6)' : '#9ca3af', fontWeight: 600 }}>
                  {card.label}
                </span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: card.highlight ? '#FFD60A' : card.color }}>
                {card.value}
              </div>
              <div style={{ fontSize: 11, color: card.highlight ? 'rgba(255,255,255,0.45)' : '#9ca3af', marginTop: 3 }}>
                {card.sub}
              </div>
            </div>
          ))}
        </div>
      </div>
 
      {/* ── My Car Listings Summary ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: '#003566', margin: 0 }}>📋 My Car Listings</h2>
          <button
            onClick={() => navigate('/dealer/my-car-listings')}
            style={{ background: 'none', border: 'none', color: '#0057b7', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            View All →
          </button>
        </div>
 
        {/* Car listing status counts */}
        {(() => {
          const statusCount = myCarListings.reduce((acc, l) => {
            acc[l.status] = (acc[l.status] || 0) + 1;
            return acc;
          }, {});
          const statusCards = [
            { key: 'submitted', label: 'Under Review', icon: '🔍', bg: '#dbeafe', color: '#1d4ed8' },
            { key: 'approved',  label: 'Approved',     icon: '✅', bg: '#dcfce7', color: '#166534' },
            { key: 'published', label: 'Live',         icon: '🟢', bg: '#d1fae5', color: '#065f46' },
            { key: 'rejected',  label: 'Rejected',     icon: '❌', bg: '#fee2e2', color: '#991b1b' },
          ];
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: myCarListings.length > 0 ? 16 : 0 }}>
              {statusCards.map(sc => (
                <div key={sc.key} style={{
                  background: '#fff', borderRadius: 12, padding: '14px 16px',
                  border: '1px solid #e5e7eb', boxShadow: '0 1px 6px rgba(0,53,102,0.05)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                    {sc.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: sc.color }}>
                      {statusCount[sc.key] || 0}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{sc.label}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
 
        {myCarListings.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 20px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: 32, margin: '0 0 8px' }}>🚗</p>
            <p style={{ fontWeight: 600, color: '#6b7280', margin: '0 0 12px', fontSize: 14 }}>No car listings yet</p>
            <button
              onClick={() => navigate('/dealer/sell-car')}
              style={{ padding: '9px 20px', background: 'linear-gradient(135deg,#003566,#001d3d)', border: '2px solid #FFD60A', borderRadius: 9, color: '#FFD60A', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              + List Your Car
            </button>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(0,53,102,0.06)' }}>
            {myCarListings.slice(0, 3).map((l, i) => {
              const STATUS_STYLE = {
                draft:     { bg: '#f3f4f6', color: '#374151', label: 'Draft' },
                submitted: { bg: '#dbeafe', color: '#1d4ed8', label: 'Under Review' },
                approved:  { bg: '#dcfce7', color: '#166534', label: 'Approved' },
                rejected:  { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
                published: { bg: '#d1fae5', color: '#065f46', label: 'Live 🟢' },
              };
              const sc = STATUS_STYLE[l.status] || STATUS_STYLE.draft;
              return (
                <div
                  key={l.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', borderBottom: i < 2 && myCarListings.length > 1 ? '1px solid #f3f4f6' : 'none',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onClick={() => navigate('/dealer/my-car-listings')}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#003566' }}>
                      {l.year} {l.brand} {l.model}
                    </span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                      {[l.fuel_type, l.transmission, l.km_driven ? `${Number(l.km_driven).toLocaleString('en-IN')} km` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {l.asking_price > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#003566' }}>
                        {fmt(l.asking_price)}
                      </span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
                      {sc.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
 
      {/* ── Recent Listings + Recent Offers ── */}
      <div className="dd-grid">
        {/* Recent Auction Listings */}
        <div className="dd-panel">
          <div className="dd-panel-head">
            <h2 className="dd-panel-title">Recent Listings</h2>
            <button className="dd-panel-link" onClick={() => navigate("/dealer/listings")}>View All</button>
          </div>
          {recentListings.length === 0 ? (
            <p className="dd-empty">No listings available.</p>
          ) : (
            <div className="dd-listing-list">
              {recentListings.map((l) => (
                <div key={l.id} className="dd-listing-row" onClick={() => navigate(`/dealer/listings/${l.id}`)}>
                  <div className="dd-listing-info">
                    <span className="dd-listing-car">{l.car?.brand} {l.car?.model}</span>
                    <span className="dd-listing-meta">{l.car?.year} · {l.car?.km_driven?.toLocaleString()} km · {l.car?.city}</span>
                  </div>
                  <div className="dd-listing-right">
                    <span className="dd-listing-price">
                      ₹{Number(l.price_range_min / 100000).toFixed(1)}–{Number(l.price_range_max / 100000).toFixed(1)}L
                    </span>
                    <span className="dd-listing-bids">{l.total_offers_count} bids</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
 
        {/* Recent Offers */}
        <div className="dd-panel">
          <div className="dd-panel-head">
            <h2 className="dd-panel-title">My Recent Offers</h2>
            <button className="dd-panel-link" onClick={() => navigate("/dealer/my-offers")}>View All</button>
          </div>
          {offers.length === 0 ? (
            <p className="dd-empty">You haven't placed any offers yet.</p>
          ) : (
            <div className="dd-offer-list">
              {offers.slice(0, 5).map((o) => (
                <div key={o.id} className="dd-offer-row">
                  <div>
                    <span className="dd-offer-car">{o.car?.brand} {o.car?.model}</span>
                    <span className="dd-offer-date">{new Date(o.created_at).toLocaleDateString("en-IN")}</span>
                  </div>
                  <div className="dd-offer-right">
                    <span className="dd-offer-price">₹{Number(o.offer_price).toLocaleString("en-IN")}</span>
                    <span className={`dd-offer-badge dd-offer-badge--${o.status} ${o.is_highest ? "dd-offer-badge--winning" : ""}`}>
                      {o.is_highest ? "🏆 Winning" : o.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
 
      {/* ── Recent Bidding Pass Purchases ── */}
      {passes.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: '#003566', margin: 0 }}>🔨 Recent Bidding Passes</h2>
            <button onClick={() => navigate('/dealer/payments')} style={{ background: 'none', border: 'none', color: '#0057b7', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              View All →
            </button>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(0,53,102,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Pass Type', 'Duration', 'Amount', 'Status', 'Expires'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {passes.slice(0, 5).map((p) => {
                  const PASS_LABELS = { day: '⚡ 1 Day', week: '🔥 Weekly', month: '👑 Monthly' };
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#003566', fontSize: 13 }}>{PASS_LABELS[p.pass_type] || p.pass_type}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{p.duration_days} day{p.duration_days > 1 ? 's' : ''}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#003566' }}>{fmt(p.amount)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                          background: p.status === 'success' ? '#dcfce7' : '#fef9c3',
                          color: p.status === 'success' ? '#166534' : '#854d0e',
                        }}>
                          {p.status === 'success' ? '✅ Paid' : '⏳ ' + p.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: p.expires_at && new Date(p.expires_at) > new Date() ? '#059669' : '#6b7280' }}>
                        {p.expires_at ? new Date(p.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}